import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Search, Users, Zap, Globe, Quote } from "lucide-react";

// Rotating client testimonials — the splash sells itself before login.
// Cycle every ~5.2s. Names and quotes intentionally short so the card can
// stay compact and readable inside the floating scatter.
const TESTIMONIALS = [
  {
    quote: "Kit rebuilt our whole online presence in 6 days. Bookings are up 41%.",
    name: "Miss Rodriguez",
    company: "Rodriguez Pest Control · Frisco, TX",
    accent: "#22C55E",
  },
  {
    quote: "The AI closes leads at 2 a.m. while I'm asleep. I've never had that before.",
    name: "David M.",
    company: "Lone Star Striping Pros · Fort Worth, TX",
    accent: "#F5B800",
  },
  {
    quote: "One dashboard, four brands. It's the operating system I always wanted.",
    name: "Chris G.",
    company: "CargoPros · Central Valley, CA",
    accent: "#2563EB",
  },
  {
    quote: "$299 for the whole site felt like a typo. It wasn't.",
    name: "Anthony R.",
    company: "Lone Star Lumpers · Dallas, TX",
    accent: "#A78BFA",
  },
];


// Client-side "living" numbers for the decorative preview cards so the
// splash feels alive, not static. Rules from Kit:
//   • Revenue: nudges up every 6s
//   • SEO Score: rotates 92–95 daily (deterministic per calendar day)
//   • CRM Pipeline: New/Qualified/Won ticks quietly
//   • Websites live: 97+ · goes up daily by 1
function seoScoreForToday() {
  const d = new Date();
  const dayIndex = d.getFullYear() * 372 + d.getMonth() * 31 + d.getDate();
  // Deterministic mod 4 → 92,93,94,95
  return 92 + (dayIndex % 4);
}
function websitesLiveForToday() {
  // 97 on Aug 9 2026, +1 per calendar day
  const start = new Date(2026, 7, 9); // Aug 9, 2026
  const today = new Date();
  const days = Math.max(0, Math.floor((today - start) / (24 * 60 * 60 * 1000)));
  return 97 + days;
}
function fmtMoney(v) { return "$" + v.toLocaleString("en-US"); }


// State-of-the-art futuristic all-white splash — "Welcome to Nari AI"
// "Nari" in soft grey · "AI" in electric green · centered ENTER cta.
// This is the front door to the private client hub (Nari AI operator console).
export default function ClientsSplash() {
  const [revenue, setRevenue] = useState(142890);
  const [pipeline, setPipeline] = useState({ new: 24, qualified: 12, won: 8 });
  const [tIdx, setTIdx] = useState(0);
  const seoScore = seoScoreForToday();
  const sitesLive = websitesLiveForToday();

  useEffect(() => {
    document.title = "Welcome to Nari AI · Client Hub";
    const prev = document.body.style.background;
    document.body.style.background = "#FFFFFF";
    // Nudge the decorative numbers so the splash feels live.
    const iv = setInterval(() => {
      setRevenue((r) => r + Math.floor(80 + Math.random() * 420));
      setPipeline((p) => ({
        new: p.new + (Math.random() < 0.4 ? 1 : 0),
        qualified: p.qualified + (Math.random() < 0.25 ? 1 : 0),
        won: p.won + (Math.random() < 0.12 ? 1 : 0),
      }));
    }, 6000);
    // Rotate the testimonial slightly out-of-phase with the number ticker.
    const tv = setInterval(() => setTIdx((i) => (i + 1) % TESTIMONIALS.length), 5200);
    return () => { document.body.style.background = prev; clearInterval(iv); clearInterval(tv); };
  }, []);

  const t = TESTIMONIALS[tIdx];

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{
        background: "#FFFFFF",
        fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
        color: "#111",
      }}
      data-testid="clients-splash"
    >
      {/* Circuit-board decorative texture — bespoke SVG pattern etched in low-contrast greys */}
      <svg
        aria-hidden="true"
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          opacity: 0.55,
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 88%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 88%)",
        }}
      >
        <defs>
          <pattern id="circuits" x="0" y="0" width="240" height="240" patternUnits="userSpaceOnUse">
            <g stroke="#D1D5DB" strokeWidth="1" fill="none" strokeLinecap="round">
              <path d="M0 40 L80 40 L100 60 L180 60 L200 80 L240 80" />
              <path d="M0 120 L60 120 L80 140 L140 140 L160 160 L240 160" />
              <path d="M0 200 L40 200 L60 180 L120 180 L140 200 L240 200" />
              <path d="M40 0 L40 40" />
              <path d="M100 60 L100 120" />
              <path d="M160 0 L160 60" />
              <path d="M200 80 L200 140" />
              <path d="M80 140 L80 200" />
              <path d="M140 140 L140 240" />
            </g>
            <g fill="#E5E7EB">
              <circle cx="40" cy="40" r="3" />
              <circle cx="100" cy="60" r="2.5" />
              <circle cx="160" cy="60" r="3" />
              <circle cx="200" cy="80" r="2.5" />
              <circle cx="60" cy="120" r="2.5" />
              <circle cx="140" cy="140" r="3" />
              <circle cx="80" cy="200" r="2.5" />
              <rect x="118" y="18" width="8" height="8" rx="1" />
              <rect x="178" y="118" width="10" height="6" rx="1" />
              <rect x="18" y="158" width="6" height="10" rx="1" />
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#circuits)" />
      </svg>

      {/* Faint precision grid — subtle depth over the circuit pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      {/* Soft green atmospheric glow behind the wordmark */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "18%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(1100px, 90vw)",
          height: "600px",
          background:
            "radial-gradient(closest-side, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0.05) 40%, transparent 75%)",
          filter: "blur(2px)",
        }}
      />

      {/* Floating dashboard preview cards — decorative, illustrate what's inside the console */}
      <FloatCard style={{ top: "14%",  right: "6%",  transform: "rotate(2deg)" }} testId="float-revenue">
        <div className="text-[9px] uppercase tracking-[0.28em] font-black text-neutral-400">Revenue this month</div>
        <div className="mt-1 font-black text-[28px] leading-none text-neutral-900 tabular-nums">{fmtMoney(revenue)}</div>
        <div className="mt-1 flex items-center gap-1.5 text-[10px] font-bold text-emerald-500">
          <TrendingUp size={11} /> +18.2% vs last month
        </div>
        <svg width="180" height="40" viewBox="0 0 180 40" className="mt-3">
          <polyline
            points="0,30 20,26 40,28 60,20 80,22 100,14 120,17 140,10 160,12 180,6"
            fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
      </FloatCard>

      <FloatCard style={{ top: "12%", right: "22%", transform: "rotate(-1deg)" }} tint="#22C55E" testId="float-seo" narrow>
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black text-neutral-400">
          <Search size={10} /> SEO score
        </div>
        <div className="mt-1 flex items-baseline gap-1">
          <div className="font-black text-[36px] leading-none tabular-nums" style={{ color: "#22C55E" }}>{seoScore}</div>
          <div className="text-[10px] font-bold text-neutral-400">/ 100</div>
        </div>
        <div className="mt-1 text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: "#22C55E" }}>
          Daily avg 92–95
        </div>
      </FloatCard>

      <FloatCard style={{ top: "38%", right: "4%", transform: "rotate(1.5deg)" }} testId="float-pipeline" narrow>
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black text-neutral-400">
          <Users size={10} /> CRM pipeline
        </div>
        <div className="mt-2 space-y-1.5 text-[11px]">
          <PipelineRow label="New"       n={pipeline.new}       color="#22C55E" />
          <PipelineRow label="Qualified" n={pipeline.qualified} color="#F5B800" />
          <PipelineRow label="Won"       n={pipeline.won}       color="#2563EB" />
        </div>
      </FloatCard>

      <FloatCard style={{ top: "44%", left: "6%", transform: "rotate(-2deg)" }} testId="float-automation" narrow>
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black text-neutral-400">
          <Zap size={10} /> Automation
        </div>
        <div className="mt-2 flex items-center gap-1 text-[10px] font-bold">
          <span className="px-2 py-0.5 rounded" style={{ background: "rgba(59,130,246,0.14)", color: "#2563EB" }}>Lead</span>
          <span className="text-neutral-400">→</span>
          <span className="px-2 py-0.5 rounded" style={{ background: "rgba(245,184,0,0.16)", color: "#B45309" }}>AI</span>
          <span className="text-neutral-400">→</span>
          <span className="px-2 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.14)", color: "#22C55E" }}>SMS</span>
        </div>
      </FloatCard>

      <FloatCard style={{ top: "62%", left: "12%", transform: "rotate(1deg)" }} testId="float-testimonial" tint={t.accent}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${t.accent}1F` }}>
            <Quote size={12} style={{ color: t.accent }} />
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] font-black text-neutral-400">
            Client testimonial
          </div>
        </div>
        <div
          key={tIdx}
          className="mt-2 text-[12px] italic text-neutral-800 leading-snug max-w-[230px] animate-quoteIn"
          data-testid="float-testimonial-quote"
        >
          &ldquo;{t.quote}&rdquo;
        </div>
        <div className="mt-2 text-[10px] font-black text-neutral-900" data-testid="float-testimonial-name">
          {t.name}
        </div>
        <div className="text-[9px] text-neutral-500">{t.company}</div>
        <div className="mt-2 flex items-center gap-1">
          {TESTIMONIALS.map((_, i) => (
            <span
              key={i}
              className="inline-block h-[3px] rounded-full transition-all"
              style={{
                width: i === tIdx ? 16 : 5,
                background: i === tIdx ? t.accent : "#E5E7EB",
              }}
            />
          ))}
        </div>
      </FloatCard>

      <FloatCard style={{ bottom: "12%", right: "10%", transform: "rotate(-1.5deg)" }} testId="float-websites" narrow>
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black text-neutral-400">
          <Globe size={10} /> Websites
        </div>
        <div className="mt-1 font-black text-[28px] leading-none text-neutral-900 tabular-nums">
          {sitesLive}<span className="text-[14px] text-emerald-500 ml-1">live</span>
        </div>
        <div className="mt-2 text-[10px] text-neutral-500 space-y-0.5">
          <div>● lonestarstriping.com</div>
          <div>● cargo-pros.com</div>
          <div>● lonestarlumper.com</div>
        </div>
      </FloatCard>

      {/* Top-left minimal chrome */}
      <div className="absolute top-6 left-6 md:top-8 md:left-10 flex items-center gap-3 z-20" data-testid="splash-top-mark">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: "#22C55E", boxShadow: "0 0 12px rgba(34,197,94,0.85)" }}
        />
        <div
          className="text-[10px] uppercase tracking-[0.32em] font-bold"
          style={{ color: "rgba(0,0,0,0.55)" }}
        >
          Nari AI · Operator Console
        </div>
      </div>

      {/* Top-right — back to public site */}
      <Link
        to="/"
        className="absolute top-6 right-6 md:top-8 md:right-10 z-20 text-[10px] uppercase tracking-[0.28em] font-bold hover:text-black transition-colors"
        style={{ color: "rgba(0,0,0,0.45)" }}
        data-testid="splash-back-home"
      >
        ← plugitai.com
      </Link>

      {/* Centered stage */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div
          className="text-[10px] md:text-[11px] uppercase tracking-[0.42em] font-black mb-8 animate-fadeUp"
          style={{ color: "rgba(0,0,0,0.5)" }}
          data-testid="splash-eyebrow"
        >
          Welcome to
        </div>

        <h1
          className="font-black tracking-[-0.045em] leading-[0.9] animate-fadeUp"
          style={{
            fontSize: "clamp(4rem, 14vw, 12rem)",
            fontFamily: "'Cabinet Grotesk', 'Inter', system-ui, sans-serif",
            animationDelay: "80ms",
          }}
          data-testid="splash-wordmark"
        >
          <span style={{ color: "#B8BEC7" }}>Nari</span>
          <span
            style={{
              color: "#22C55E",
              textShadow: "0 0 24px rgba(34,197,94,0.55), 0 0 60px rgba(34,197,94,0.28)",
            }}
          >
            AI
          </span>
        </h1>

        <p
          className="mt-10 max-w-[540px] text-sm md:text-base leading-relaxed animate-fadeUp"
          style={{ color: "rgba(0,0,0,0.55)", animationDelay: "180ms" }}
          data-testid="splash-tagline"
        >
          The operating hub for every company you run. Launch bots, spin up tools,
          orchestrate the whole portfolio from one glass.
        </p>

        <Link
          to="/clients/login"
          className="group mt-14 inline-flex items-center gap-4 px-14 py-6 rounded-full font-black uppercase tracking-[0.32em] text-sm animate-fadeUp"
          style={{
            background: "#0A0A0A",
            color: "#FFFFFF",
            boxShadow:
              "0 24px 60px -18px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.6), 0 0 30px -6px rgba(34,197,94,0.4)",
            animationDelay: "280ms",
            transition: "transform 220ms ease, box-shadow 220ms ease, letter-spacing 220ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.letterSpacing = "0.38em";
            e.currentTarget.style.boxShadow =
              "0 30px 80px -18px rgba(0,0,0,0.4), 0 0 0 1px rgba(0,0,0,0.6), 0 0 44px -4px rgba(34,197,94,0.65)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.letterSpacing = "0.32em";
            e.currentTarget.style.boxShadow =
              "0 24px 60px -18px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.6), 0 0 30px -6px rgba(34,197,94,0.4)";
          }}
          data-testid="splash-enter-btn"
        >
          <span>Enter</span>
          <ArrowRight
            size={18}
            style={{ color: "#22C55E", transition: "transform 220ms ease" }}
          />
        </Link>

        <div
          className="mt-12 text-[10px] uppercase tracking-[0.32em] animate-fadeUp"
          style={{ color: "rgba(0,0,0,0.35)", animationDelay: "380ms" }}
          data-testid="splash-footer-note"
        >
          Powered by Nariai · a PlugitAI system
        </div>
      </div>

      {/* Corner registration marks — that "engineered" feel */}
      {["tl", "tr", "bl", "br"].map((pos) => (
        <div
          key={pos}
          className="absolute w-6 h-6"
          style={{
            top: pos.startsWith("t") ? 20 : "auto",
            bottom: pos.startsWith("b") ? 20 : "auto",
            left: pos.endsWith("l") ? 20 : "auto",
            right: pos.endsWith("r") ? 20 : "auto",
            borderTop: pos.startsWith("t") ? "1px solid rgba(0,0,0,0.25)" : "none",
            borderBottom: pos.startsWith("b") ? "1px solid rgba(0,0,0,0.25)" : "none",
            borderLeft: pos.endsWith("l") ? "1px solid rgba(0,0,0,0.25)" : "none",
            borderRight: pos.endsWith("r") ? "1px solid rgba(0,0,0,0.25)" : "none",
          }}
        />
      ))}

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeUp { animation: fadeUp 640ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes quoteIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .animate-quoteIn { animation: quoteIn 380ms cubic-bezier(0.16, 1, 0.3, 1) both; }
      `}</style>
    </div>
  );
}

// Floating dashboard preview card. Positioned absolutely by the caller so we
// can scatter them decoratively around the splash. Hidden below `lg` so the
// centered wordmark isn't crowded on tablets/phones.
function FloatCard({ children, style, testId, tint, narrow }) {
  const rot = (style && style.transform) || "";
  return (
    <div
      className="hidden lg:block absolute pointer-events-none"
      style={{
        ...style,
        width: narrow ? 200 : 260,
        transform: rot,
      }}
      data-testid={testId}
    >
      <div
        className="rounded-xl bg-white p-4"
        style={{
          border: `1px solid ${tint ? `${tint}44` : "#E5E7EB"}`,
          boxShadow: "0 20px 40px -20px rgba(15,23,42,0.15), 0 6px 12px -6px rgba(15,23,42,0.08)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PipelineRow({ label, n, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-600">{label}</span>
      <span
        className="tabular-nums px-2 py-0.5 rounded font-black text-[10px]"
        style={{ background: `${color}22`, color }}
      >
        {n}
      </span>
    </div>
  );
}
